"use client";

import { COLOR, IllustrationCard } from "@/components/learn/chapter-1/_shared";
import { useDocLocale } from "@/components/shared/docs-blocks";
import type { Locale } from "@/lib/i18n";

// ── Single accent color — only cost varies, everything else is neutral ──


const STRINGS: Record<
  Locale,
  {
    fundName: (key: string) => string;
    expenseRatio: string;
    cumulativeCost: string;
    approxCost: (man: number) => string;
    footer: string;
    title: string;
    description: string;
  }
> = {
  ja: {
    fundName: (key) => `ファンド${key}`,
    expenseRatio: "信託報酬",
    cumulativeCost: "100万円を20年運用したときの累計コスト",
    approxCost: (man) => `約 ${man.toFixed(1)} 万円`,
    footer: "中身が同じなら、信託報酬の差がそのまま長期のコスト差になる",
    title: "似た中身なら、コスト差が長期で効いてくる",
    description:
      "いずれも同じ指数に連動するファンドでも、信託報酬が違えば長期のコスト差は大きくなります（100万円を保有し続け、運用益や積立を無視した単純概算）。",
  },
  en: {
    fundName: (key) => `Fund ${key}`,
    expenseRatio: "Expense ratio",
    cumulativeCost: "Cumulative cost on ¥1,000,000 held for 20 years",
    approxCost: (man) => `approx. ¥${(man * 10000).toLocaleString("en-US")}`,
    footer: "With the same holdings inside, the gap in expense ratio becomes the long-term cost gap",
    title: "With similar holdings, the cost gap compounds over time",
    description:
      "Even for funds tracking the same index, a difference in expense ratio widens the long-term cost gap (a simple approximation holding ¥1,000,000 and ignoring returns and further contributions).",
  },
};
const ACCENT = COLOR.rose;

// ── Fund data ──────────────────────────────────────────────────────

type Fund = {
  key: string;
  expense: number; // annual expense ratio (%)
  cost20y: number; // cumulative cost over 20 years per 100万円 (万円)
};

const FUNDS: Fund[] = [
  { key: "A" as const, expense: 0.08, cost20y: 1.6 },
  { key: "B" as const, expense: 0.22, cost20y: 4.4 },
  { key: "C" as const, expense: 0.55, cost20y: 11.0 },
];

// ── Illustration ───────────────────────────────────────────────────

function CostComparisonIllustration() {
  const t = STRINGS[useDocLocale()];
  const VW = 460;
  const headerH = 18;
  const rowH = 44;
  const rowGap = 4;
  const footerH = 26;
  const VH = headerH + FUNDS.length * rowH + (FUNDS.length - 1) * rowGap + footerH;

  const labelX = 14;
  const nameW = 64;
  const expenseX = labelX + nameW + 10;
  const expenseW = 62;
  const barX = expenseX + expenseW + 14;
  const barMaxW = VW - barX - 70;
  const maxCost = Math.max(...FUNDS.map((f) => f.cost20y));

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="mx-auto w-full max-w-xl">
      {/* Column headers */}
      <text
        x={expenseX + expenseW / 2}
        y={headerH - 4}
        textAnchor="middle"
        fontSize="8"
        fill="currentColor"
        fillOpacity={0.45}
      >
        {t.expenseRatio}
      </text>
      <text x={barX} y={headerH - 4} fontSize="8" fill="currentColor" fillOpacity={0.45}>
        {t.cumulativeCost}
      </text>

      {FUNDS.map((fund, i) => {
        const rowY = headerH + i * (rowH + rowGap);
        const rowCy = rowY + rowH / 2;
        const barH = 18;
        const barY = rowCy - barH / 2;
        const barW = (fund.cost20y / maxCost) * barMaxW;

        return (
          <g key={fund.key}>
            {/* Fund name */}
            <text x={labelX} y={rowCy + 4} fontSize="11" fill="currentColor" fontWeight="600">
              {t.fundName(fund.key)}
            </text>

            {/* Expense ratio (neutral) */}
            <text
              x={expenseX + expenseW / 2}
              y={rowCy + 4}
              textAnchor="middle"
              fontSize="13"
              fill="currentColor"
              fontWeight="700"
            >
              {fund.expense.toFixed(2)}%
            </text>

            {/* Cost bar (single accent color — length is the message) */}
            <rect
              x={barX}
              y={barY}
              width={barW}
              height={barH}
              rx={2}
              fill={ACCENT}
              fillOpacity={0.22}
              stroke={ACCENT}
              strokeWidth="1"
              strokeOpacity={0.55}
            />
            <text x={barX + barW + 6} y={barY + barH / 2 + 3} fontSize="10.5" fill={ACCENT} fontWeight="700">
              {t.approxCost(fund.cost20y)}
            </text>
          </g>
        );
      })}

      {/* Footer message */}
      <text x={VW / 2} y={VH - 8} textAnchor="middle" fontSize="9.5" fill="currentColor" fontWeight="600">
        {t.footer}
      </text>
    </svg>
  );
}

// ── Export ─────────────────────────────────────────────────────────

export function DocFundCostComparisonDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard
      title={t.title}
      description={t.description}
    >
      <CostComparisonIllustration />
    </IllustrationCard>
  );
}
